package com.dotdrives.web.pages.givers

import com.dotdrives.web.pages.dashboard.money
import com.dotdrives.controller.GiverFilters
import com.dotdrives.controller.GiversUiState
import com.dotdrives.design.DotAvatarPalette
import com.dotdrives.design.DotTone
import com.dotdrives.model.GiverSummary
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotButton
import com.dotdrives.web.ui.DotPill
import com.dotdrives.web.ui.DotSkeleton
import react.ChildrenBuilder
import react.FC
import react.Key
import react.Props
import react.dom.aria.AriaPressed
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.select
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.strong
import web.cssom.ClassName
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.text

external interface GiversTableProps : Props {
    var state: GiversUiState
    var currentUserId: String
    var canWrite: Boolean
    var canFinance: Boolean
    var onSearch: (String) -> Unit
    var onFilter: (GiverFilters) -> Unit
    var onPage: (Int) -> Unit
    var onOpen: (GiverSummary) -> Unit
    var onRetry: () -> Unit
    var onAdd: () -> Unit
    var onImport: () -> Unit
    var onDownloadTemplate: () -> Unit
}

val GiversTable = FC<GiversTableProps> { props ->
    val state = props.state
    val availableTags = state.items.flatMap(GiverSummary::tags).distinct().sorted()

    div {
        className = ClassName("givers-controls")
        input {
            className = ClassName("givers-search")
            type = InputType.text
            value = state.query
            placeholder = "⌕ Search givers…"
            ariaLabel = "Search givers"
            onChange = { props.onSearch(it.currentTarget.value) }
        }
        button {
            type = ButtonType.buttonType
            className = ClassName(
                "givers-filter" + if (state.filters.stewardUserId != null) " givers-filter-active" else "",
            )
            ariaPressed = (state.filters.stewardUserId != null).unsafeCast<AriaPressed>()
            onClick = {
                props.onFilter(
                    state.filters.copy(
                        stewardUserId = if (state.filters.stewardUserId == null) props.currentUserId else null,
                    ),
                )
            }
            +(if (state.filters.stewardUserId != null) "Steward: Me ✕" else "+ Steward: Me")
        }
        select {
            className = ClassName(
                "givers-filter" + if (state.filters.giverType != null) " givers-filter-active" else "",
            )
            ariaLabel = "Filter by giver type"
            value = state.filters.giverType ?: ""
            onChange = {
                props.onFilter(
                    state.filters.copy(giverType = it.currentTarget.value.takeIf(String::isNotBlank)),
                )
            }
            option {
                value = ""
                +"+ Type"
            }
            option {
                value = "individual"
                +"Individual"
            }
            option {
                value = "organization"
                +"Organization"
            }
        }
        select {
            className = ClassName(
                "givers-filter" + if (state.filters.tag != null) " givers-filter-active" else "",
            )
            ariaLabel = "Filter by tag"
            value = state.filters.tag ?: ""
            disabled = availableTags.isEmpty()
            title = if (availableTags.isEmpty()) "No tags on this page" else ""
            onChange = {
                props.onFilter(state.filters.copy(tag = it.currentTarget.value.takeIf(String::isNotBlank)))
            }
            option {
                value = ""
                +"+ Tag"
            }
            availableTags.forEach { tag ->
                option {
                    value = tag
                    +tag
                }
            }
        }
    }

    state.error?.let { error ->
        DotBanner {
            message = error
            onRetry = props.onRetry
            className = "givers-inline-banner"
        }
    }

    when {
        state.loading && state.items.isEmpty() -> GiversSkeleton()
        state.items.isEmpty() && state.error == null -> GiversEmpty {
            canWrite = props.canWrite
            onAdd = props.onAdd
            onImport = props.onImport
            onDownloadTemplate = props.onDownloadTemplate
        }
        state.items.isNotEmpty() -> {
            div {
                className = ClassName("givers-table-head" + if (!props.canFinance) " givers-no-finance" else "")
                div { +"GIVER" }
                div { +"STEWARD" }
                if (props.canFinance) { div { +"LIFETIME / THIS YEAR" }; div { +"LAST GIFT" } }
                div { +"TAGS" }
            }
            div {
                className = ClassName("givers-table-body")
                state.items.forEach { giver ->
                    val (avatarBackground, avatarText) = DotAvatarPalette.tintFor(giver.id)
                    val avatarStyle = js("({})")
                    avatarStyle.backgroundColor = avatarBackground
                    avatarStyle.color = com.dotdrives.web.ui.accessibleAvatarText(avatarText)
                    button {
                        type = ButtonType.buttonType
                        className = ClassName("givers-table-row" + if (!props.canFinance) " givers-no-finance" else "")
                        onClick = { props.onOpen(giver) }

                        div {
                            className = ClassName("givers-person")
                            div {
                                className = ClassName("givers-avatar")
                                style = avatarStyle
                                +giverInitials(giver.displayName)
                            }
                            div {
                                className = ClassName("givers-person-copy")
                                div {
                                    className = ClassName("givers-person-name")
                                    +giver.displayName
                                    if (!giver.isActive) +" · Inactive"
                                }
                                div {
                                    className = ClassName("givers-person-meta")
                                    +"${giver.giverType.replaceFirstChar { it.uppercase() }} · Steward: ${giver.stewardName ?: "Unassigned"}"
                                }
                            }
                        }
                        div {
                            className = ClassName("givers-steward")
                            +(giver.stewardName ?: "Unassigned")
                        }
                        if (props.canFinance) {
                            div { className = ClassName("givers-money")
                                +(giver.lifetime?.let(::money) ?: "—")
                                div { className = ClassName("givers-year-total"); +(giver.yearTotal?.let(::money) ?: "—"); +" this year" }
                            }
                            div { className = ClassName("givers-money"); +(giver.lastGiftAt?.toString()?.take(10) ?: "No gifts") }
                        }
                        div {
                            className = ClassName("givers-tags")
                            giver.tags.forEach { tag ->
                                DotPill {
                                    text = tag
                                    tone = DotTone.Automation
                                    className = "givers-tag"
                                }
                            }
                        }
                    }
                }
            }
            GiversPagination {
                currentPage = state.page
                totalPages = state.totalPages
                onPage = props.onPage
            }
        }
    }
}

private val GiversSkeleton = FC<Props> {
    div {
        className = ClassName("givers-table-body")
        repeat(7) {
            div {
                className = ClassName("givers-table-row")
                DotSkeleton { height = "36px" }
                DotSkeleton {
                    width = "72%"
                    height = "14px"
                }
                DotSkeleton {
                    width = "42px"
                    height = "14px"
                }
                DotSkeleton {
                    width = "42px"
                    height = "14px"
                }
                DotSkeleton {
                    width = "54px"
                    height = "22px"
                }
            }
        }
    }
}

private external interface GiversEmptyProps : Props {
    var canWrite: Boolean
    var onAdd: () -> Unit
    var onImport: () -> Unit
    var onDownloadTemplate: () -> Unit
}

private val GiversEmpty = FC<GiversEmptyProps> { props ->
    div {
        className = ClassName("givers-empty")
        div {
            div {
                className = ClassName("givers-empty-emoji")
                ariaHidden = true
                +"👥"
            }
            div {
                className = ClassName("givers-empty-title")
                +"Your givers live here"
            }
            div {
                className = ClassName("givers-empty-copy")
                +"Bring your existing list over in one go, or add people as you meet them."
            }
            if (props.canWrite) {
                div {
                    className = ClassName("givers-empty-actions")
                    DotButton {
                        text = "⇧ Import CSV"
                        onClick = props.onImport
                        className = "givers-topbar-button"
                    }
                    DotButton {
                        text = "+ Add one giver"
                        onClick = props.onAdd
                        variant = com.dotdrives.design.DotButtonVariant.Secondary
                        className = "givers-topbar-button"
                    }
                }
            }
            button {
                type = ButtonType.buttonType
                className = ClassName("givers-template-link")
                onClick = { props.onDownloadTemplate() }
                +"⇩ "
                strong { +"Download the CSV template" }
                +" — name, email, phone, address"
            }
        }
    }
}

private external interface GiversPaginationProps : Props {
    var currentPage: Int
    var totalPages: Int
    var onPage: (Int) -> Unit
}

private val GiversPagination = FC<GiversPaginationProps> { props ->
    if (props.totalPages > 1) {
        div {
            className = ClassName("givers-pagination")
            PageButton("←", props.currentPage > 1) { props.onPage(props.currentPage - 1) }
            paginationWindow(props.currentPage, props.totalPages).forEachIndexed { index, page ->
                if (page == null) {
                    div {
                        key = "ellipsis-$index".unsafeCast<Key>()
                        className = ClassName("givers-ellipsis")
                        +"…"
                    }
                } else {
                    PageButton(page.toString(), true, page == props.currentPage) { props.onPage(page) }
                }
            }
            PageButton("→", props.currentPage < props.totalPages) { props.onPage(props.currentPage + 1) }
        }
    }
}

private fun ChildrenBuilder.PageButton(
    text: String,
    enabled: Boolean,
    active: Boolean = false,
    onClick: () -> Unit,
) {
    button {
        type = ButtonType.buttonType
        className = ClassName(
            "givers-page-button" + if (active) " givers-page-button-active" else "",
        )
        disabled = !enabled
        ariaLabel = when (text) {
            "←" -> "Previous page"
            "→" -> "Next page"
            else -> "Page $text"
        }
        this.onClick = { onClick() }
        +text
    }
}

internal fun giverInitials(displayName: String): String =
    displayName.split(' ', '&')
        .filter(String::isNotBlank)
        .take(2)
        .joinToString("") { it.first().uppercase() }
        .ifBlank { "?" }
